import { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { wl } from './theme';

interface Props {
  title: string;
  onClose: () => void;
  children: ReactNode;
  maxWidth?: number;
  accent?: string;
}

// Shared modal shell for every create/edit form in this module — same
// backdrop + card pattern as wallet-intel/CategoryExplorerModal.tsx, pulled
// out once since Keywords/Profiles/Takedowns each need one.
export function Modal({ title, onClose, children, maxWidth = 640, accent = wl.green }: Props) {
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(3,5,3,0.84)', backdropFilter: 'blur(4px)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 10 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.97 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          style={{ background: wl.bg, border: `1px solid ${accent}40`, boxShadow: `0 0 60px ${accent}18`, borderRadius: 10, width: '100%', maxWidth, maxHeight: '86vh', overflowY: 'auto', fontFamily: wl.font }}
        >
          <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: `1px solid ${wl.border}` }}>
            <h2 style={{ color: wl.text, fontWeight: 800, fontSize: 15 }}>{title}</h2>
            <button onClick={onClose} style={{ color: wl.textMuted }} className="hover:text-white transition-colors">
              <X size={18} />
            </button>
          </div>
          <div className="px-6 py-5">{children}</div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Shared field primitives so every form in this module looks identical
// without re-typing the same inline styles everywhere.
export function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label style={{ color: wl.textMuted, fontSize: 11, letterSpacing: '0.04em', fontWeight: 700, display: 'block', marginBottom: 6, fontFamily: wl.font }}>
      {children}
    </label>
  );
}

const inputStyle: React.CSSProperties = {
  background: wl.bg, border: `1.5px solid ${wl.border}`, color: wl.text, fontSize: 13,
  borderRadius: 6, padding: '9px 12px', width: '100%', outline: 'none', fontFamily: wl.font,
};

export function TextInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{ ...inputStyle, ...props.style }}
      onFocus={(e) => { e.currentTarget.style.borderColor = wl.green; props.onFocus?.(e); }}
      onBlur={(e) => { e.currentTarget.style.borderColor = wl.border; props.onBlur?.(e); }}
    />
  );
}

export function TextArea(props: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea
      {...props}
      style={{ ...inputStyle, resize: 'vertical', minHeight: 70, ...props.style }}
      onFocus={(e) => { e.currentTarget.style.borderColor = wl.green; props.onFocus?.(e); }}
      onBlur={(e) => { e.currentTarget.style.borderColor = wl.border; props.onBlur?.(e); }}
    />
  );
}

export function PrimaryButton({ children, disabled, onClick, type = 'button' }: {
  children: ReactNode; disabled?: boolean; onClick?: () => void; type?: 'button' | 'submit';
}) {
  return (
    <motion.button
      type={type}
      whileHover={disabled ? {} : { scale: 1.02 }}
      whileTap={disabled ? {} : { scale: 0.98 }}
      onClick={onClick}
      disabled={disabled}
      className="px-5 py-2.5 rounded font-bold tracking-wide transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
      style={{ background: wl.greenWash, color: wl.green, border: `1px solid ${wl.green}44`, fontSize: 12, fontFamily: wl.font }}
    >
      {children}
    </motion.button>
  );
}

export function GhostButton({ children, onClick, destructive = false }: { children: ReactNode; onClick?: () => void; destructive?: boolean }) {
  const c = destructive ? wl.critical : wl.textMuted;
  return (
    <button
      onClick={onClick}
      className="px-4 py-2 rounded font-semibold transition-colors"
      style={{ background: 'transparent', color: c, border: `1px solid ${destructive ? wl.critical + '40' : wl.border}`, fontSize: 12, fontFamily: wl.font }}
    >
      {children}
    </button>
  );
}
